import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest } from "../services/index";

const API_URL = "/api/annual-personal-objectives";

// Create objectives (Admin) - POST /
export const createObjectives = createAsyncThunk(
  "objectives/create",
  async (objectivesData, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(API_URL, objectivesData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Accept objective (Teacher) - PUT /accept/:objectiveID
export const acceptObjective = createAsyncThunk(
  "objectives/accept",
  async ({ objectiveId, teacherNotes }, { rejectWithValue }) => {
    try {
      const body = teacherNotes ? { teacherNotes } : {};
      const response = await hrmsUpdateRequest(`${API_URL}/accept/${objectiveId}`, body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Reject objective (Teacher) - PUT /reject/:objectiveID
export const rejectObjective = createAsyncThunk(
  "objectives/reject",
  async ({ objectiveId, teacherNotes }, { rejectWithValue }) => {
    try {
      const body = teacherNotes ? { teacherNotes } : {};
      const response = await hrmsUpdateRequest(`${API_URL}/reject/${objectiveId}`, body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update objective (Admin) - PUT /update/:objectiveID
export const updateObjective = createAsyncThunk(
  "objectives/update",
  async ({ objectiveId, updateData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${API_URL}/update/${objectiveId}`, updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get all objectives (Admin) - GET /
export const fetchAllObjectives = createAsyncThunk(
  "objectives/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(API_URL);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get objectives by status (Admin) - GET /status/:status
export const fetchObjectivesByStatus = createAsyncThunk(
  "objectives/fetchByStatus",
  async (status, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/status/${status}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get objectives by year (Admin) - GET /year/:year
export const fetchObjectivesByYear = createAsyncThunk(
  "objectives/fetchByYear",
  async (year, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/year/${year}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get objectives by teacher email (Teacher/Admin) - GET /teacher/:email
export const fetchObjectivesByTeacher = createAsyncThunk(
  "objectives/fetchByTeacher",
  async (teacherEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  objectives: [],
  count: {
    accepted: 0,
    rejected: 0,
    pending: 0,
  },
  loading: false,
  error: null,
};

const annualPersonalObjectiveSlice = createSlice({
  name: "annualPersonalObjective",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetObjectives: (state) => {
      state.objectives = [];
      state.count = {
        accepted: 0,
        rejected: 0,
        pending: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Create objectives
      .addCase(createObjectives.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createObjectives.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.objectives) {
          state.objectives = [...state.objectives, ...action.payload.objectives];
        }
      })
      .addCase(createObjectives.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Accept objective
      .addCase(acceptObjective.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptObjective.fulfilled, (state, action) => {
        state.loading = false;
        const updatedObjective = action.payload;
        state.objectives = state.objectives.map((obj) =>
          obj.id === updatedObjective.id ? updatedObjective : obj
        );
        // Update counts
        state.count = {
          accepted: state.objectives.filter((o) => o.status === "accepted" || o.status === "completed").length,
          rejected: state.objectives.filter((o) => o.status === "rejected").length,
          pending: state.objectives.filter((o) => o.status === "pending").length,
        };
      })
      .addCase(acceptObjective.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reject objective
      .addCase(rejectObjective.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectObjective.fulfilled, (state, action) => {
        state.loading = false;
        const updatedObjective = action.payload;
        state.objectives = state.objectives.map((obj) =>
          obj.id === updatedObjective.id ? updatedObjective : obj
        );
        // Update counts
        state.count = {
          accepted: state.objectives.filter((o) => o.status === "accepted" || o.status === "completed").length,
          rejected: state.objectives.filter((o) => o.status === "rejected").length,
          pending: state.objectives.filter((o) => o.status === "pending").length,
        };
      })
      .addCase(rejectObjective.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update objective
      .addCase(updateObjective.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateObjective.fulfilled, (state, action) => {
        state.loading = false;
        const updatedObjective = action.payload;
        state.objectives = state.objectives.map((obj) =>
          obj.id === updatedObjective.id ? updatedObjective : obj
        );
        // Update counts
        state.count = {
          accepted: state.objectives.filter((o) => o.status === "accepted" || o.status === "completed").length,
          rejected: state.objectives.filter((o) => o.status === "rejected").length,
          pending: state.objectives.filter((o) => o.status === "pending").length,
        };
      })
      .addCase(updateObjective.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch all objectives
      .addCase(fetchAllObjectives.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllObjectives.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.objectives) {
          state.objectives = action.payload.objectives;
          state.count = action.payload.count || {
            accepted: action.payload.objectives.filter((o) => o.status === "accepted" || o.status === "completed").length,
            rejected: action.payload.objectives.filter((o) => o.status === "rejected").length,
            pending: action.payload.objectives.filter((o) => o.status === "pending").length,
          };
        }
      })
      .addCase(fetchAllObjectives.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by status
      .addCase(fetchObjectivesByStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchObjectivesByStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.objectives) {
          state.objectives = action.payload.objectives;
          state.count = action.payload.count || {
            accepted: action.payload.objectives.filter((o) => o.status === "accepted" || o.status === "completed").length,
            rejected: action.payload.objectives.filter((o) => o.status === "rejected").length,
            pending: action.payload.objectives.filter((o) => o.status === "pending").length,
          };
        }
      })
      .addCase(fetchObjectivesByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by year
      .addCase(fetchObjectivesByYear.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchObjectivesByYear.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.objectives) {
          state.objectives = action.payload.objectives;
          state.count = action.payload.count || {
            accepted: action.payload.objectives.filter((o) => o.status === "accepted" || o.status === "completed").length,
            rejected: action.payload.objectives.filter((o) => o.status === "rejected").length,
            pending: action.payload.objectives.filter((o) => o.status === "pending").length,
          };
        }
      })
      .addCase(fetchObjectivesByYear.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by teacher
      .addCase(fetchObjectivesByTeacher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchObjectivesByTeacher.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.objectives) {
          state.objectives = action.payload.objectives;
          state.count = action.payload.count || {
            accepted: action.payload.objectives.filter((o) => o.status === "accepted" || o.status === "completed").length,
            rejected: action.payload.objectives.filter((o) => o.status === "rejected").length,
            pending: action.payload.objectives.filter((o) => o.status === "pending").length,
          };
        }
      })
      .addCase(fetchObjectivesByTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetObjectives } = annualPersonalObjectiveSlice.actions;
export default annualPersonalObjectiveSlice.reducer;

